import { OutboundSystemView } from "@redotech/redo-fulfillment-operations-common/views/default-outbound-views";
import { useTranslation } from "@redotech/redo-i18n";
import { viewTypes } from "@redotech/redo-views-common/views";
import { sinkPromise } from "@redotech/util/promise";
import { memo, useEffect, useState } from "react";
import { RedoButton } from "./arbiter-components/buttons/redo-button";
import {
  CheckboxValue,
  RedoCheckbox,
} from "./arbiter-components/checkbox/redo-checkbox";
import { RedoTextInput } from "./arbiter-components/input/redo-text-input";
import { Flex } from "./flex";
import { DisabledTooltip } from "./tooltip/disabled-tooltip";
import { getLocalizedViewNameValidationError } from "./view-name-validation";

export const TableFilterEditorHeader = memo(function TableFilterEditorHeader({
  reservedViewNames,
  activeView,
  cancel,
  save,
  savePending,
  viewNotificationsEnabled,
}: {
  reservedViewNames: Set<string>;
  activeView: viewTypes[keyof viewTypes];
  cancel: () => void;
  save: (body: {
    name: string;
    showNotification?: boolean;
    hideWhenEmpty?: boolean;
  }) => Promise<void>;
  savePending: boolean;
  viewNotificationsEnabled?: boolean;
}) {
  const { t } = useTranslation();
  const [viewName, setViewName] = useState<string>("");
  const [hasEditedViewName, setHasEditedViewName] = useState(false);
  const [showNotification, setShowNotification] =
    useState<CheckboxValue>(false);
  const [hideWhenEmpty, setHideWhenEmpty] = useState<CheckboxValue>(false);

  useEffect(() => {
    setHasEditedViewName(false);
    if (activeView && activeView.name !== OutboundSystemView.CREATE) {
      setViewName(activeView.name);
      if (viewNotificationsEnabled) {
        setShowNotification(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (activeView as viewTypes["returns"]).showNotification,
        );
        setHideWhenEmpty(
          // eslint-disable-next-line @typescript-eslint/no-unsafe-type-assertion
          (activeView as viewTypes["returns"]).hideWhenEmpty ?? false,
        );
      }
    }
  }, [activeView, viewNotificationsEnabled]);

  const submitDisabledBecauseOfEmptyName =
    viewName.length === 0 ? t("You must name this view") : false;

  // "create" is reserved for the new-view route.
  const nameIsUnchanged =
    activeView.name !== OutboundSystemView.CREATE &&
    viewName === activeView.name;
  const submitDisabledBecauseOfInvalidName =
    getLocalizedViewNameValidationError(t, viewName, {
      skipReservedCheck: nameIsUnchanged,
    }) ?? false;

  const submitDisabledBecauseOfDuplicateName = reservedViewNames.has(
    viewName.toLowerCase(),
  )
    ? t("A view with this name already exists")
    : false;

  const submitDisabled =
    submitDisabledBecauseOfEmptyName ||
    submitDisabledBecauseOfInvalidName ||
    submitDisabledBecauseOfDuplicateName;
  const viewNameError = hasEditedViewName
    ? submitDisabled || undefined
    : undefined;

  const handleViewNameChange = (name: string) => {
    setViewName(name);
    setHasEditedViewName(true);
  };

  return (
    <Flex align="center" justify="space-between" px="3xl" py="3xl">
      <Flex align="center" gap="xl">
        <Flex basis="200px" shrink={0}>
          <RedoTextInput
            errorText={viewNameError}
            fullWidth
            placeholder={t("Enter a name")}
            setValue={handleViewNameChange}
            state={viewNameError ? "error" : "default"}
            value={viewName}
          />
        </Flex>
        {viewNotificationsEnabled && (
          <>
            <RedoCheckbox
              label={t("Enable notifications")}
              setValue={setShowNotification}
              size="sm"
              value={showNotification}
            />
            <RedoCheckbox
              label={t("Hide when empty")}
              setValue={(value) => {
                setHideWhenEmpty(value);
              }}
              size="sm"
              value={hideWhenEmpty}
            />
          </>
        )}
      </Flex>
      <Flex align="center">
        <RedoButton hierarchy="secondary" onClick={cancel} text={t("Cancel")} />
        <DisabledTooltip disabledMessage={submitDisabled}>
          <RedoButton
            dataTestId="save-view-button"
            disabled={!!submitDisabled}
            hierarchy="primary"
            onClick={() => {
              sinkPromise(
                save({
                  name: viewName,
                  showNotification:
                    viewNotificationsEnabled &&
                    showNotification.valueOf() === true,
                  hideWhenEmpty:
                    viewNotificationsEnabled &&
                    hideWhenEmpty.valueOf() === true,
                }),
              );
            }}
            pending={savePending}
            text={t("Save view")}
          />
        </DisabledTooltip>
      </Flex>
    </Flex>
  );
});
